import { byAccessor } from '../../../util'

import { ISafetyCase } from '../../../interfaces'

export type ISearchFilter = {
  searchText: string
  filterType?: string
  statusIds: number[]
  searchTagIds: number[]
  riskLevels: number[]
  sortBy?: number
  isSortByAsc: boolean
}

export const queryParamsToParams = (queryParams?: {
  [key: string]: any
}): {
  searchFilter: ISearchFilter
  pagination: { pageSize: number; pageIndex: number }
} => ({
  searchFilter: {
    statusIds: queryParams?.searchFilter?.statusIds || [],
    searchTagIds: queryParams?.searchFilter?.searchTagIds || [],
    riskLevels: queryParams?.searchFilter?.riskLevels || [],
    filterType: queryParams?.searchFilter?.filterType || undefined,
    sortBy: queryParams?.searchFilter?.sortBy || 1,
    isSortByAsc: queryParams?.searchFilter?.isSortByAsc !== undefined ? queryParams?.searchFilter?.isSortByAsc : false,
    searchText: queryParams?.searchFilter?.searchText || '',
  },
  pagination: {
    pageSize: queryParams?.pagination?.pageSize || 10,
    pageIndex: queryParams?.pagination?.pageIndex || 0,
  },
})

export const filterSafetyCase = (safetyCases: ISafetyCase[] | undefined, searchFilter: ISearchFilter) => {
  let filteredSafetyCases = safetyCases || []

  const q = String(searchFilter.searchText).toLowerCase().trim()

  if (q.length > 0) {
    filteredSafetyCases = filteredSafetyCases.filter(
      safetyCase =>
        safetyCase.name?.toLowerCase().includes(q) ||
        safetyCase.description?.toLowerCase().includes(q) ||
        safetyCase.parentStatus?.name?.toLowerCase().includes(q) ||
        safetyCase.tags?.some(tag => tag.name.toLowerCase().includes(q)) ||
        safetyCase.selectedRiskMatrixCell?.riskLabel?.toLowerCase().includes(q) ||
        safetyCase.lastUpdatedByName?.toLowerCase().includes(q),
    )
  }

  if (searchFilter.statusIds?.length > 0) {
    filteredSafetyCases = filteredSafetyCases.filter(
      safetyCase => searchFilter.statusIds.indexOf(safetyCase.parentStatus.id) > -1,
    )
  }

  if (searchFilter.searchTagIds?.length > 0) {
    filteredSafetyCases = filteredSafetyCases.filter(
      safetyCase =>
        safetyCase.tags &&
        safetyCase.tags.some(tag => searchFilter.searchTagIds.indexOf(tag.id) > -1),
    )
  } 

  if (searchFilter.riskLevels?.length > 0) {
    filteredSafetyCases = filteredSafetyCases.filter(
      safetyCase =>
        safetyCase.selectedRiskMatrixCell?.riskLevel &&
        searchFilter.riskLevels.indexOf(safetyCase.selectedRiskMatrixCell.riskLevel) > -1,
    )
  }

  if (searchFilter.sortBy) {
    switch (searchFilter.sortBy) {
      case 1: filteredSafetyCases = [...filteredSafetyCases].sort((safetyCase1, safetyCase2) =>
        // Sort by createdAt
        byAccessor(
          safetyCase1,
          safetyCase2,
          (safetyCase: ISafetyCase) => safetyCase.createdAt,
          searchFilter.isSortByAsc,
        ),
      )
        break
      case 2:
        // Sort by status, then by createdAt as a secondary sort
        filteredSafetyCases = [...filteredSafetyCases].sort((safetyCase1, safetyCase2) => {
          const statusOrder = byAccessor(
            safetyCase1,
            safetyCase2,
            (safetyCase: ISafetyCase) => safetyCase.parentStatus?.sortOrder,
            searchFilter.isSortByAsc,
          );
          if (statusOrder !== 0) return statusOrder;
          return byAccessor(
            safetyCase1,
            safetyCase2,
            (safetyCase: ISafetyCase) => safetyCase.createdAt,
            false, // Default to descending order for createdAt
          );
        });
        break
      case 3:
        // Sort by risk level, then by createdAt as a secondary sort
        filteredSafetyCases = [...filteredSafetyCases].sort((safetyCase1, safetyCase2) => {
          const riskOrder = byAccessor(
            safetyCase1,
            safetyCase2,
            (safetyCase: ISafetyCase) => safetyCase.selectedRiskMatrixCell?.riskLevel,
            searchFilter.isSortByAsc,
          );
          if (riskOrder !== 0) return riskOrder;
          return byAccessor(
            safetyCase1,
            safetyCase2,
            (safetyCase: ISafetyCase) => safetyCase.createdAt,
            false, // Default to descending order for createdAt
          );
        });
        break
      default:
        break
    }
  }

  return filteredSafetyCases
}
